import { Flex, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { BizyTableNode } from '../../types/nodes';
import { BizyAnalyticsEvent, useBizyAnalytics } from '../context/analytics-context';
import { BIZY_KIT_I18N_NAMESPACE } from '../i18n';
import { exportFilename } from '../lib/export-filename';
import { BizyTableShell } from '../lib/table-shell';
import { TruncationCaption } from '../lib/truncation-caption';
import { useEvidenceDrill } from '../lib/use-evidence-drill';
import type { BizyNodeProps } from './node-props';

type TablePayload = BizyTableNode['payload'];
type Column = TablePayload['columns'][number];
type Cell = string | number | null;

const NUMERIC_FORMATS = new Set(['number', 'percent', 'currency']);

function formatCell(value: Cell | undefined, format: Column['format']): string {
	if (value == null) return '';
	switch (format) {
		case 'percent':
			return typeof value === 'number' ? `${value}%` : String(value);
		case 'currency':
			return typeof value === 'number' ? `$${value.toLocaleString()}` : String(value);
		case 'number':
			return typeof value === 'number' ? value.toLocaleString() : String(value);
		default:
			return String(value);
	}
}

function toNumber(value: Cell | undefined): number {
	if (typeof value === 'number') return value;
	if (typeof value === 'string') return Number(value.replace(/[$,%\s]/g, ''));
	return NaN;
}

function toDate(value: Cell | undefined): number {
	const s = String(value ?? '');
	if (!/[A-Za-z]{3,}.*\d{4}|\d{4}-\d{2}/.test(s)) return NaN;
	return Date.parse(s);
}

function firstNumber(value: Cell | undefined): number {
	const match = /-?\d[\d,]*\.?\d*/.exec(String(value ?? ''));
	return match ? Number(match[0].replace(/,/g, '')) : NaN;
}

/**
 * Renders the Data Agent's Table node — typed columns + positional rows — with
 * the renderer owning alignment, number/percent/currency formatting, and
 * client-side column sorting. When rows were trimmed a caption discloses the
 * "top N of M".
 */
export function TableNode({ node }: BizyNodeProps) {
	const { payload } = node as BizyTableNode;
	const { title, columns, rows, totalRows, selection, defaultSortColumn } = payload;
	const { t } = useTranslation(BIZY_KIT_I18N_NAMESPACE);
	const { track, conversationId, messageId } = useBizyAnalytics();
	const { drillEnabled, drill, expand } = useEvidenceDrill();
	const [sort, setSort] = useState<{ col: number; dir: 'asc' | 'desc' } | null>(
		defaultSortColumn != null && defaultSortColumn >= 0 && defaultSortColumn < columns.length ? { col: defaultSortColumn, dir: 'desc' } : null
	);

	const sortedRows = useMemo(() => {
		const indexed = rows.map((row, originalIndex) => ({ row, originalIndex }));
		if (!sort) return indexed;
		const { col, dir } = sort;
		const declaredNumeric = NUMERIC_FORMATS.has(columns[col]?.format ?? '');
		const numeric = declaredNumeric && indexed.some(({ row }) => row.cells[col] != null && !Number.isNaN(toNumber(row.cells[col])));
		const factor = dir === 'asc' ? 1 : -1;
		const isMissing = (v: Cell | undefined) => v == null || (numeric && Number.isNaN(toNumber(v)));
		return [...indexed].sort((entryA, entryB) => {
			const av = entryA.row.cells[col];
			const bv = entryB.row.cells[col];
			const aMissing = isMissing(av);
			const bMissing = isMissing(bv);
			if (aMissing && bMissing) return 0;
			if (aMissing) return 1;
			if (bMissing) return -1;
			let cmp: number;
			if (numeric) {
				cmp = toNumber(av) - toNumber(bv);
			} else {
				const ad = toDate(av);
				const bd = toDate(bv);
				const an = firstNumber(av);
				const bn = firstNumber(bv);
				if (!Number.isNaN(ad) && !Number.isNaN(bd)) {
					cmp = ad - bd;
				} else if (!Number.isNaN(an) && !Number.isNaN(bn)) {
					cmp = an - bn;
				} else {
					cmp = String(av).localeCompare(String(bv));
				}
			}
			return cmp * factor;
		});
	}, [rows, sort, columns]);

	if (columns.length === 0) return null;

	const onDrill = drillEnabled ? (label: string) => drill('table', label, { title }) : undefined;
	const onExpand = drillEnabled && totalRows != null ? () => expand('table', totalRows as number, { title, noun: columns[0]?.label }) : undefined;

	const onSort = (col: number) => {
		setSort((prev) => {
			const next = prev?.col === col ? { col, dir: prev.dir === 'asc' ? ('desc' as const) : ('asc' as const) } : { col, dir: 'desc' as const };
			track(BizyAnalyticsEvent.TableSorted, {
				column: columns[col]?.label ?? String(col),
				direction: next.dir,
				conversationId,
				messageId,
				path: window.location.pathname,
			});
			return next;
		});
	};

	return (
		<Flex direction="column" gap={1} mb={4} data-testid="bizy-table">
			{title ? (
				<Text fontSize="md" fontWeight="semibold" color="text.default">
					{title}
				</Text>
			) : null}
			<BizyTableShell
				filename={exportFilename(title, 'table')}
				caption={
					totalRows != null && totalRows > rows.length ? (
						<TruncationCaption shown={rows.length} total={totalRows} selection={selection} onExpand={onExpand} />
					) : null
				}
			>
				<Thead>
					<Tr>
						{columns.map((col, i) => {
							const active = sort?.col === i;
							return (
								<Th
									key={i}
									textAlign={col.align}
									cursor="pointer"
									userSelect="none"
									aria-sort={active ? (sort?.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
									onClick={() => onSort(i)}
								>
									{col.label}
									{active ? (sort?.dir === 'asc' ? ' \u25b2' : ' \u25bc') : ''}
								</Th>
							);
						})}
					</Tr>
				</Thead>
				<Tbody>
					{sortedRows.map(({ row, originalIndex }) => {
						const drillLabel = String(row.cells[0] ?? '');
						return (
							<Tr
								key={originalIndex}
								{...(onDrill && drillLabel
									? {
											onClick: () => onDrill(drillLabel),
											cursor: 'pointer',
											'aria-label': t('drillInto', { label: drillLabel, defaultValue: 'Drill into {{label}}' }),
											_hover: { bg: 'secondary.bgAlpha' },
										}
									: {})}
							>
								{columns.map((col, ci) => (
									<Td key={ci} textAlign={col.align}>
										{formatCell(row.cells[ci], col.format)}
									</Td>
								))}
							</Tr>
						);
					})}
				</Tbody>
			</BizyTableShell>
		</Flex>
	);
}
